import { Link } from '@tanstack/react-router';
import { ExternalLink, LayoutTemplate, FileText } from 'lucide-react';
import { useSetStatus } from './hooks.ts';
import { apiErrorCode } from './api.ts';
import type { SiteBundle } from './api.ts';
import { useAuthStore } from '../../store/auth.ts';

const PUBLIC_BASE = 'site.wapplify.app';

function mandatedTotal(bundle: SiteBundle): number {
  return bundle.groups.filter((g) => g.mandated).reduce((n, g) => n + g.fields.length, 0);
}

// Shared header across Builder & Content Manager: identity, completeness,
// publish control, and a link across to the other tool.
export function SiteTopBar({ bundle, tool }: { bundle: SiteBundle; tool: 'builder' | 'content' }): JSX.Element {
  const setStatus = useSetStatus();
  const orgName = useAuthStore((s) => s.user?.orgName);
  const { site } = bundle;
  const published = site.status === 'PUBLISHED';
  const publicUrl = `https://${site.subdomain}.${PUBLIC_BASE}`;
  const name = (site.theme.nameEn ?? '').trim() || (site.theme.nameDv ?? '').trim() || orgName || site.subdomain;

  const total = mandatedTotal(bundle);
  const filled = Math.max(0, total - bundle.complianceGaps.length);
  const pct = total === 0 ? 100 : Math.round((filled / total) * 100);
  const allDone = bundle.complianceGaps.length === 0;

  return (
    <header className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-border bg-paper/70 px-4 py-2.5">
      <div className="flex min-w-0 items-center gap-2.5">
        <h1 className="truncate font-serif text-lg text-ink">{name}</h1>
        <span className={`pill ${published ? 'pill-success' : 'pill-pending'}`}>{published ? 'Published' : 'Draft'}</span>
      </div>

      <div className="hidden items-center gap-2 sm:flex">
        <div className="h-1.5 w-28 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: allDone ? 'hsl(var(--success))' : 'hsl(var(--primary))' }} />
        </div>
        <span className="mono-meta text-xs text-muted-foreground">{filled}/{total}</span>
      </div>

      <div className="ms-auto flex items-center gap-1.5">
        <Link
          to={tool === 'builder' ? '/content' : '/'}
          className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm font-medium text-foreground/80 hover:bg-muted"
        >
          {tool === 'builder' ? <><FileText size={15} /> Content</> : <><LayoutTemplate size={15} /> Builder</>}
        </Link>
        <a href={publicUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-lg px-2.5 py-1.5 text-sm text-primary hover:bg-primary/10">
          View site <ExternalLink size={13} />
        </a>
        <button
          type="button"
          className={published ? 'btn-secondary btn-sm' : 'btn-primary btn-sm'}
          disabled={setStatus.isPending}
          onClick={() => setStatus.mutate(published ? 'DRAFT' : 'PUBLISHED')}
        >
          {setStatus.isPending ? 'Saving…' : published ? 'Unpublish' : 'Publish'}
        </button>
        {setStatus.isError && <span className="text-xs text-destructive">Failed{apiErrorCode(setStatus.error) ? ` (${apiErrorCode(setStatus.error)})` : ''}</span>}
      </div>
    </header>
  );
}
